import React from 'react';
import { AppBar, Toolbar, Typography, Button, Container, Box } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HomeIcon from '@mui/icons-material/Home';
import ImageIcon from '@mui/icons-material/Image';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

function Navbar() {
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    {
      text: 'Home',
      path: '/',
      icon: <HomeIcon sx={{ mr: 0.5 }} />
    },
    {
      text: 'Image Generator',
      path: '/image-generator',
      icon: <ImageIcon sx={{ mr: 0.5 }} />
    },
    {
      text: 'Pricing',
      path: '/pricing',
      icon: <PaymentIcon sx={{ mr: 0.5 }} />
    },
    {
      text: 'Profile',
      path: '/profile',
      icon: <AccountCircleIcon sx={{ mr: 0.5 }} />,
      authRequired: true
    }
  ];

  const buttonStyle = (path) => ({
    color: isActive(path) ? '#00ffea' : 'rgba(255, 255, 255, 0.85)',
    position: 'relative',
    fontWeight: 500,
    fontSize: '0.95rem',
    textTransform: 'none',
    letterSpacing: '0.5px',
    px: 2,
    py: 1.5,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      color: '#00ffea',
      backgroundColor: 'rgba(0, 255, 234, 0.08)'
    },
    '&:after': isActive(path) ? {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '30%',
      height: '3px',
      backgroundColor: '#00ffea',
      borderRadius: '3px'
    } : {}
  });

  return (
    <AppBar 
      position="static" 
      sx={{ 
        background: 'linear-gradient(180deg, rgba(10, 11, 14, 0.95) 0%, rgba(10, 11, 14, 0.9) 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ py: 1 }}>
          <Typography 
            variant="h5" 
            component={RouterLink} 
            to="/"
            sx={{ 
              flexGrow: 1,
              textDecoration: 'none',
              color: '#00ffea',
              fontWeight: 700,
              letterSpacing: '1px',
              fontFamily: "'Inter', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': {
                color: '#00b8ff'
              }
            }}
          >
            <ImageIcon sx={{ fontSize: '2rem' }} />
            TRIPOD AI
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            '& .MuiButton-root': {
              borderRadius: '8px',
            }
          }}>
            {menuItems.map((item) => (
              (!item.authRequired || isAuthenticated) && (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  sx={buttonStyle(item.path)}
                >
                  {item.text}
                </Button>
              )
            ))}

            {isAuthenticated ? (
              <Button
                onClick={handleSignOut}
                sx={{
                  ...buttonStyle('/signout'),
                  ml: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    border: '1px solid #00ffea',
                    backgroundColor: 'rgba(0, 255, 234, 0.08)'
                  }
                }}
              >
                Sign Out
              </Button>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/signin"
                  sx={{
                    ...buttonStyle('/signin'),
                    ml: 2
                  }}
                >
                  Sign In
                </Button>
                <Button
                  component={RouterLink}
                  to="/signup"
                  sx={{
                    color: '#000',
                    bgcolor: '#00ffea',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      bgcolor: '#00b8ff'
                    }
                  }}
                >
                  Sign Up
                </Button>
              </>
            )}

            <Box sx={{ 
              height: 24, 
              width: '1px', 
              bgcolor: 'rgba(255, 255, 255, 0.1)', 
              mx: 2 
            }} />
            
            <Button
              component={RouterLink}
              to="/contact"
              sx={buttonStyle('/contact')}
            >
              Contact
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar; 